import { ArrowDownCircle, Award, BarChart3, BookOpen, Bot, CalendarRange, ClipboardList, FileSpreadsheet, FileText, Landmark, LayoutDashboard, LayoutGrid, LogOut, Map, Menu, MessageCircle, Moon, PackageCheck, PiggyBank, Receipt, Shield, ShieldCheck, Smartphone, Sun, Users, Wallet, X } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ADMIN_ROLES, ALL_ROLES, FUNDING_ROLES, Role, WRITE_ROLES, useAuth } from "../lib/auth";
import { AiChatWidget } from "./AiChatWidget";
import { api } from "../lib/api";
import { cn } from "./ui";

const NAV: { group: string; items: { to: string; label: string; icon: any; roles: Role[]; id: string; badge?: string }[] }[] = [
  {
    group: "Dashboard",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ALL_ROLES, id: "dashboard" },
      { to: "/npl", label: "Analisis Col 1–5", icon: BarChart3, roles: ALL_ROLES, id: "npl" },
      { to: "/npl-map", label: "NPL Map", icon: Map, roles: ALL_ROLES, id: "npl-map" },
    ],
  },
  { group: "Master Data", items: [{ to: "/siswa", label: "Siswa & Pinjaman", icon: Users, roles: ALL_ROLES, id: "siswa" }] },
  {
    group: "Pinjaman",
    items: [
      { to: "/matriks", label: "Matriks Cicilan", icon: LayoutGrid, roles: ALL_ROLES, id: "matriks" },
      { to: "/approval", label: "Approval", icon: ShieldCheck, roles: WRITE_ROLES, id: "approval" },
    ],
  },
  {
    group: "Transaksi",
    items: [
      { to: "/pembayaran", label: "Pembayaran", icon: Receipt, roles: ALL_ROLES, id: "pembayaran" },
      { to: "/kas", label: "Kas Harian", icon: Wallet, roles: ALL_ROLES, id: "kas" },
      { to: "/kas-bulanan", label: "Rekap Kas Bulanan", icon: CalendarRange, roles: ALL_ROLES, id: "kas-bulanan" },
      { to: "/pengeluaran", label: "Pengeluaran Koperasi", icon: ArrowDownCircle, roles: ALL_ROLES, id: "pengeluaran" },
      // Arus Kas Koperasi: ditunda publikasinya — aktifkan kembali dengan menghapus komentar baris di bawah
      // { to: "/arus-kas", label: "Arus Kas Koperasi", icon: Waves, roles: ALL_ROLES, id: "arus-kas" },
      { to: "/dokumen", label: "Kwitansi & Tagihan", icon: FileText, roles: ALL_ROLES, id: "dokumen" },
      { to: "/pengembalian-dokumen", label: "Pengembalian Dok.", icon: PackageCheck, roles: ALL_ROLES, id: "pengembalian-dokumen", badge: "doc-returns" },
      { to: "/import-export", label: "Import / Export", icon: FileSpreadsheet, roles: ADMIN_ROLES, id: "import-export" },
    ],
  },
  { group: "Simpanan", items: [{ to: "/simpanan", label: "Simpanan Anggota", icon: PiggyBank, roles: ALL_ROLES, id: "simpanan" }, { to: "/shu", label: "SHU", icon: Award, roles: ALL_ROLES, id: "shu" }] },
  { group: "Komunikasi", items: [{ to: "/whatsapp", label: "WhatsApp Resmi", icon: MessageCircle, roles: ALL_ROLES, id: "whatsapp" }] },
  { group: "Laporan", items: [
    { to: "/laporan", label: "Executive Summary", icon: ClipboardList, roles: ALL_ROLES, id: "laporan" },
    { to: "/funding", label: "Funding Management", icon: Landmark, roles: FUNDING_ROLES, id: "funding" },
    { to: "/asisten-ai", label: "Asisten AI", icon: Bot, roles: FUNDING_ROLES, id: "asisten-ai" },
  ] },
  { group: "Panduan", items: [{ to: "/panduan", label: "Panduan Sistem", icon: BookOpen, roles: ALL_ROLES, id: "panduan" }] },
  { group: "Administrasi", items: [
    { to: "/admin", label: "User & Konfigurasi", icon: Shield, roles: ["super_admin"], id: "admin" },
    { to: "/monitoring-mobile", label: "Monitoring Mobile Siswa", icon: Smartphone, roles: ADMIN_ROLES, id: "monitoring-mobile" },
  ] },
];

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  return (
    <button
      data-testid="theme-toggle-button"
      onClick={() => setTheme(dark ? "light" : "dark")}
      className="inline-flex h-9 w-9 items-center justify-center rounded-md border bg-card hover:bg-muted transition-colors"
      title={dark ? "Mode terang" : "Mode gelap"}
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pendingDocs, setPendingDocs] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => { api.get("/doc-returns/summary").then((r) => setPendingDocs(r.data.pending || 0)).catch(() => {}); }, [pathname]);

  const sidebar = (
    <aside className="flex h-full w-64 flex-col bg-[hsl(var(--sidebar))] text-slate-200">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
        <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center font-display font-extrabold text-primary-foreground">Y</div>
        <div>
          <p className="font-display font-bold leading-tight text-white">Yutaka</p>
          <p className="text-[11px] tracking-wider uppercase text-slate-400">Edu-Finance</p>
        </div>
        <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close-button">
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {NAV.map((g) => {
          const items = g.items.filter((i) => user && i.roles.includes(user.role));
          if (!items.length) return null;
          return (
            <div key={g.group} className="space-y-0.5 py-4 border-b border-white/10 last:border-b-0" data-testid={`sidebar-group-${g.group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{g.group}</p>
              {items.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.to === "/"}
                  data-testid={`sidebar-nav-${i.id}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors", isActive ? "bg-primary/20 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white")
                  }
                >
                  <i.icon className="h-4 w-4 shrink-0" />
                  {i.label}
                  {i.badge === "doc-returns" && pendingDocs > 0 && <span className="ml-auto rounded-full bg-sky-500 text-white text-[10px] font-bold px-1.5 py-0.5 num" data-testid="sidebar-doc-returns-badge">{pendingDocs}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="border-t border-white/10 p-4">
        <p className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="text-xs text-slate-400 truncate">{user?.email}</p>
        <span className="mt-2 inline-block rounded bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-slate-200" data-testid="sidebar-user-role">{user?.role_label}</span>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0">{sidebar}</div>
        </div>
      )}
      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-card/95 backdrop-blur px-4 sm:px-6">
          <button className="lg:hidden rounded-md p-2 hover:bg-muted" onClick={() => setOpen(true)} data-testid="sidebar-open-button">
            <Menu className="h-5 w-5" />
          </button>
          <p className="text-sm text-muted-foreground hidden sm:block">Monitoring pinjaman & cicilan koperasi</p>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <button onClick={logout} data-testid="logout-button" className="inline-flex h-9 items-center gap-2 rounded-md border bg-card px-3 text-sm font-medium hover:bg-muted transition-colors">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Keluar</span>
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      {user && FUNDING_ROLES.includes(user.role) && <AiChatWidget />}
    </div>
  );
}
